import { CANONICAL_COLUMN_IDS, CLIENT_I18N_KEY, normalizeLabel } from '@features/event-level-column-rename/constants';
import type { Translate } from 'next-translate';

import type {
  CanonicalColumnId,
  ColumnLabelResolver,
  EventSettableColumnId,
} from '@features/event-level-column-rename/types';

/**
 * Client mirror of bidops-app's `app/validators/line_item_column_labels_validator.rb`, so a
 * buyer sees the offending field go red before the save is sent. The server stays authoritative:
 * a 422 still renders as the summary list. If the server rules change, change these to match —
 * every constant below is copied from that file.
 */
export const MAX_LABEL_LENGTH = 100;
const FORMULA_PREFIXES = ['=', '+', '-', '@'];
const HTML_TAG = /<[^>]*>/;

/** Another column's display name a proposed label must not read as. */
export interface OtherColumnLabel {
  columnId: CanonicalColumnId;
  label: string;
}

export type LabelValidationResult =
  | { otherColumnId: CanonicalColumnId; reason: 'duplicate' }
  | { reason: 'html_not_allowed'
  | 'invalid_start'
  | 'too_long' }
  | { reason: 'reserved_spreadsheet_header'; reservedHeaderName: string }
  | null;

/**
 * Fixed spreadsheet headers a display name may not read as. Every download this platform
 * produces prints some headers that are not renameable (ids, lot columns, rank and award
 * columns, per-sheet defaults) and every upload parser keys on some of them; a display name
 * equal to one — case- and whitespace-insensitively — would put two identical headers on a
 * sheet, or make a parser read the renamed column as the fixed one. One list, so the server
 * counterpart can copy it. Source for each entry is tabled in the PR that introduced it
 * (POLARIS-3370); keep this list and that table in step.
 */
export const RESERVED_SPREADSHEET_HEADER_NAMES: readonly string[] = [
  'All-In Price',
  'All-In Price Rank',
  'Allocated Quantity',
  'Arkestro Internal ID',
  'Available Items',
  'Available Units',
  'Awarded Line Item Price',
  'Awarded Quantity',
  'Baseline Items per Unit',
  'Baseline Per Unit Price',
  'Baseline Price per Unit',
  'Baseline Quantity of Units',
  'Baseline Total Price',
  'Currency Code',
  'Current Agreed Price per Unit',
  'Current Rank',
  'Discount',
  'Extended Price - Savings Amount',
  'Extended Price - Savings Percent',
  'Is Required',
  'Line Item ID',
  'Line Item No.',
  'Logistics Charge',
  'Lot ID',
  'Lot Name',
  'Lot No.',
  'Name',
  'No',
  'No Bid',
  'No Quote',
  'Original Items Per Unit',
  'Original Suggested Price Per Unit',
  'Per Item Quoted Price',
  'Proposed Price',
  'Proposed Price per Unit',
  'Quoted Items Per Unit',
  'Quoted Quantity of Items',
  'Request ID',
  'Request Name',
  'Savings',
  'Savings %',
  'Savings Amount',
  'Savings Percent',
  'Score',
  'Select Awardees Here',
  'Status',
  'Submission Date',
  'Suggested Price',
  'Supplier Company Name',
  'Supplier External ID',
  'Supplier ID',
  'Supplier\'s Items per Unit',
  'Supplier\'s Quantity of Units',
  'Supplier\'s Submitted Price per Unit',
  'Supplier\'s Total Price Submission',
  'Total All-in Price - Savings Amount',
  'Total All-in Price - Savings Percent',
  'Total Bid',
  'Total Line Item Price',
  'Weighted Score',
];

const NORMALIZED_RESERVED_SPREADSHEET_HEADER_NAMES = new Set(RESERVED_SPREADSHEET_HEADER_NAMES.map(normalizeLabel));

const isReservedSpreadsheetHeaderName = (normalizedLabel: string): boolean => (
  NORMALIZED_RESERVED_SPREADSHEET_HEADER_NAMES.has(normalizedLabel)
);

// Mirrors Ruby's [[:cntrl:]] (C0 controls and DEL) without a control-character regex, which the
// linter rejects in both literal and constructor form.
const isControlCharacter = (character: string): boolean => {
  const characterCode = character.charCodeAt(0);

  return characterCode < 0x20 || characterCode === 0x7f;
};

const hasUnsafeLeadingCharacter = (label: string): boolean => (
  FORMULA_PREFIXES.some(formulaPrefix => label.startsWith(formulaPrefix)) || Array.from(label).some(isControlCharacter)
);

/**
 * Applies the server's per-label rules in the server's order, then the reserved-header rule
 * (client-only today — see `RESERVED_SPREADSHEET_HEADER_NAMES`), then the collision rule. Returns the first
 * failure, or `null` when the label is acceptable.
 *
 * A value that trims to empty is a request to clear the override, never an error — the payload
 * builder turns it into an explicit `null`. Pass in `reserved` the names this label must not
 * collide with (other columns' current or proposed names and platform defaults); the caller
 * decides which of those apply, this function only compares them case- and
 * whitespace-insensitively, exactly as the server does.
 */
export const validateColumnLabel = (proposedLabel: string, otherColumnLabels: OtherColumnLabel[]): LabelValidationResult => {
  const label = proposedLabel.trim();

  if (label === '') return null;
  if (Array.from(label).length > MAX_LABEL_LENGTH) return { reason: 'too_long' };
  if (hasUnsafeLeadingCharacter(label)) return { reason: 'invalid_start' };
  if (HTML_TAG.test(label)) return { reason: 'html_not_allowed' };

  const normalizedProposedLabel = normalizeLabel(label);

  if (isReservedSpreadsheetHeaderName(normalizedProposedLabel)) {
    return { reason: 'reserved_spreadsheet_header', reservedHeaderName: label };
  }

  const collidingColumnLabel = otherColumnLabels.find(({ label: other }) => normalizeLabel(other) === normalizedProposedLabel);

  return collidingColumnLabel ? { otherColumnId: collidingColumnLabel.columnId, reason: 'duplicate' } : null;
};

export interface LabelsReservedByOtherColumnsParams {
  /** The column being validated; never reserved against itself. */
  columnId: EventSettableColumnId;
  /** Current effective label for a column, as the grids print it today. */
  effectiveLabelFor: ColumnLabelResolver;
  /** The form's current formValuesByColumnId, keyed by column. */
  formValuesByColumnId: Partial<Record<CanonicalColumnId, string>>;
  /** Whether this batch proposes a new name for the column (editable, and its value differs from what the drawer opened with). */
  isLabelProposedForColumn: (_columnId: CanonicalColumnId) => boolean;
  /** Platform default label for a column, in the buyer's locale. */
  platformDefaultLabelFor: (_columnId: CanonicalColumnId) => string;
}

/**
 * The names `columnId` must not collide with, mirroring the server's collision rule: a column
 * this batch renames is judged only on its proposed name; any other column — untouched, disabled,
 * or the org-only negotiation column — reserves both its current effective name and its platform
 * default. A proposed clear (dirty but empty) falls in the second group, since the client cannot
 * see the org label that would surface.
 */
export const labelsReservedByOtherColumns = ({
  columnId,
  platformDefaultLabelFor,
  isLabelProposedForColumn,
  effectiveLabelFor,
  formValuesByColumnId,
}: LabelsReservedByOtherColumnsParams): OtherColumnLabel[] => (
  CANONICAL_COLUMN_IDS.flatMap((otherId): OtherColumnLabel[] => {
    if (otherId === columnId) return [];

    const proposed = isLabelProposedForColumn(otherId) ? (formValuesByColumnId[otherId] ?? '').trim() : '';

    if (proposed !== '') return [{ columnId: otherId, label: proposed }];

    return [
      { columnId: otherId, label: effectiveLabelFor(otherId) },
      { columnId: otherId, label: platformDefaultLabelFor(otherId) },
    ];
  })
);

/**
 * The inline message for a failed rule, worded like the server's `line_items.column_labels.errors.*`
 * so the two experiences read the same. Columns are named by platform default, as the server does.
 */
export const labelValidationErrorMessage = (
  result: NonNullable<LabelValidationResult>,
  columnId: EventSettableColumnId,
  t: Translate
): string => {
  const validationMessageKeyPrefix = 'bid_request.event_config_menu.column_labels.validation';
  const platformDefaultColumnName = t(CLIENT_I18N_KEY[columnId]);

  if (result.reason === 'duplicate') {
    return t(`${validationMessageKeyPrefix}.duplicate`, { column: platformDefaultColumnName, other: t(CLIENT_I18N_KEY[result.otherColumnId]) });
  }

  if (result.reason === 'reserved_spreadsheet_header') {
    return t(`${validationMessageKeyPrefix}.reserved_spreadsheet_header`, {
      column: platformDefaultColumnName,
      header: result.reservedHeaderName,
    });
  }

  return t(`${validationMessageKeyPrefix}.${result.reason}`, { column: platformDefaultColumnName, max: MAX_LABEL_LENGTH });
};
